import { useState, useCallback, useEffect, useMemo } from "react";
import { Drawer } from "./Drawer";
import { Button } from "../atoms/Button";
import { Input } from "../atoms/Input";
import { PhoneNumberInput } from "../molecules/PhoneNumberInput";
import { InputWithDropdown } from "../molecules/InputWithDropdown";
import { Building } from "lucide-react";
import type { RepresentativeFormData } from "../../types/representative";
import {
  fetchLocationStates,
  fetchLocationCities,
  fetchLocationZips,
  fetchLocationCounty,
} from "../../services/locationReferenceService";
import {
  getEmailFieldError,
  getPhoneFieldError,
  validateLegalEntityForm
} from "../../utils/validation/legalEntityValidation";
import { resolveLocationFieldResets } from "../../utils/validation/locationConsistency";

type AddRepresentativeDetailsDrawerProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: (formData: RepresentativeFormData) => Promise<void>;
};

const emptyForm: RepresentativeFormData = {
  name: "",
  title: "",
  company: "",
  streetAddress: "",
  city: "",
  state: "",
  county: "",
  zip: "",
  phone: "",
  email: "",
};

export const AddRepresentativeDetailsDrawer = ({
  isOpen,
  onClose,
  onSave
}: AddRepresentativeDetailsDrawerProps) => {
  const [formData, setFormData] = useState<RepresentativeFormData>(emptyForm);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [locationStates, setLocationStates] = useState<string[]>([]);
  const [locationCities, setLocationCities] = useState<string[]>([]);
  const [locationZips, setLocationZips] = useState<string[]>([]);
  const [touched, setTouched] = useState<Partial<Record<keyof RepresentativeFormData, boolean>>>({});
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  const formErrors = useMemo(() => validateLegalEntityForm(formData), [formData]);
  const hasValidationErrors = Object.keys(formErrors).length > 0;

  const visibleErrors = useMemo(() => {
    if (submitAttempted) {
      return formErrors;
    }
    const v: typeof formErrors = {};
    (["name", "title", "company", "streetAddress", "city", "state", "zip"] as const).forEach(
      (k) => {
        if (touched[k] && formErrors[k]) {
          v[k] = formErrors[k];
        }
      }
    );
    if (phoneTouched) {
      const e = getPhoneFieldError(formData.phone);
      if (e) {
        v.phone = e;
      }
    }
    if (emailTouched) {
      const e = getEmailFieldError(formData.email);
      if (e) {
        v.email = e;
      }
    }
    return v;
  }, [formData, formErrors, submitAttempted, touched, phoneTouched, emailTouched]);

  const resetForm = useCallback(() => {
    setFormData(emptyForm);
    setSubmitAttempted(false);
    setIsSubmitting(false);
    setSubmitError(null);
    setLocationCities([]);
    setLocationZips([]);
    setTouched({});
    setPhoneTouched(false);
    setEmailTouched(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const states = await fetchLocationStates();
        if (!cancelled) {
          setLocationStates(states);
        }
      } catch {
        if (!cancelled) {
          setLocationStates([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    const st = formData.state.trim();
    if (!st) {
      setLocationCities([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const cities = await fetchLocationCities(st);
        if (!cancelled) {
          setLocationCities(cities);
        }
      } catch {
        if (!cancelled) {
          setLocationCities([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.state]);

  useEffect(() => {
    const st = formData.state.trim();
    const cy = formData.city.trim();
    if (!st || !cy) {
      setLocationZips([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const zips = await fetchLocationZips(st, cy);
        if (!cancelled) {
          setLocationZips(zips);
        }
      } catch {
        if (!cancelled) {
          setLocationZips([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.state, formData.city]);

  useEffect(() => {
    const st = formData.state.trim();
    const cy = formData.city.trim();
    const z = formData.zip.trim();
    if (!st || !cy || !z) {
      setFormData((prev) => (prev.county ? { ...prev, county: "" } : prev));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const county = await fetchLocationCounty(st, cy, z);
        if (!cancelled) {
          setFormData((prev) => ({ ...prev, county: county ?? "" }));
        }
      } catch {
        if (!cancelled) {
          setFormData((prev) => ({ ...prev, county: "" }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.state, formData.city, formData.zip]);

  const handleSubmit = useCallback(async () => {
    setSubmitAttempted(true);
    const validationErrors = validateLegalEntityForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      await onSave(formData);
      resetForm();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create representative details";
      setSubmitError(message);
      setIsSubmitting(false);
    }
  }, [formData, onSave, onClose, resetForm]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Representative Details"
      className="px-0"
      headerIconContainerClassName="bg-[#009ce5] text-white"
      headerIcon={<Building className="h-5 w-5" />}
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label
                htmlFor="representative-contact-name"
                className="block text-sm font-medium text-gray-700"
              >
                Name <span className="text-status-error">*</span>
              </label>
              <Input
                id="representative-contact-name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
                placeholder="Enter contact name"
                aria-required="true"
                aria-invalid={!!visibleErrors.name}
                aria-describedby={visibleErrors.name ? "name-error" : "name-helper"}
                className="bg-white"
              />
              {visibleErrors.name ? (
                <p id="name-error" className="text-sm text-status-error">
                  {visibleErrors.name}
                </p>
              ) : (
                <p id="name-helper" className="text-sm text-gray-500">
                  Enter the full contact name
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="representative-title"
                className="block text-sm font-medium text-gray-700"
              >
                Title <span className="text-status-error">*</span>
              </label>
              <Input
                id="representative-title"
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                onBlur={() => setTouched((prev) => ({ ...prev, title: true }))}
                placeholder="Enter title"
                aria-invalid={!!visibleErrors.title}
              />
              {visibleErrors.title && <p className="text-sm text-status-error">{visibleErrors.title}</p>}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="representative-company"
                className="block text-sm font-medium text-gray-700"
              >
                Company <span className="text-status-error">*</span>
              </label>
              <Input
                id="representative-company"
                value={formData.company}
                onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
                onBlur={() => setTouched((prev) => ({ ...prev, company: true }))}
                placeholder="Enter company"
                aria-invalid={!!visibleErrors.company}
              />
              {visibleErrors.company && <p className="text-sm text-status-error">{visibleErrors.company}</p>}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="representative-street"
                className="block text-sm font-medium text-gray-700"
              >
                Street Address <span className="text-status-error">*</span>
              </label>
              <Input
                id="representative-street"
                value={formData.streetAddress}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, streetAddress: e.target.value }))
                }
                onBlur={() => setTouched((prev) => ({ ...prev, streetAddress: true }))}
                placeholder="Enter street address"
                aria-invalid={!!visibleErrors.streetAddress}
              />
              {visibleErrors.streetAddress && (
                <p className="text-sm text-status-error">{visibleErrors.streetAddress}</p>
              )}
            </div>

            <InputWithDropdown
              id="representative-state"
              label="State"
              required
              options={locationStates}
              value={formData.state}
              error={visibleErrors.state}
              placeholder="Enter state"
              onChange={(state) => {
                const resets = resolveLocationFieldResets(
                  formData.state,
                  state,
                  formData.city,
                  formData.city
                );
                setFormData((prev) => ({
                  ...prev,
                  state,
                  city: resets.resetCity ? "" : prev.city,
                  zip: resets.resetZip ? "" : prev.zip,
                  county: "",
                }));
                setLocationZips([]);
              }}
              onBlur={() => setTouched((prev) => ({ ...prev, state: true }))}
            />

            <InputWithDropdown
              id="representative-city"
              label="City"
              required
              options={locationCities}
              value={formData.city}
              error={visibleErrors.city}
              placeholder={formData.state.trim() ? "Enter city" : "Enter state first"}
              disabled={!formData.state.trim()}
              onChange={(city) => {
                const resets = resolveLocationFieldResets(
                  formData.state,
                  formData.state,
                  formData.city,
                  city
                );
                setFormData((prev) => ({
                  ...prev,
                  city,
                  zip: resets.resetZip ? "" : prev.zip,
                  county: "",
                }));
                setLocationZips([]);
              }}
              onBlur={() => setTouched((prev) => ({ ...prev, city: true }))}
            />

            <InputWithDropdown
              id="representative-zip"
              label="Zip"
              required
              type="tel"
              inputMode="numeric"
              maxLength={5}
              pattern="[0-9]*"
              options={locationZips}
              value={formData.zip}
              error={visibleErrors.zip}
              placeholder={formData.city.trim() ? "Enter ZIP" : "Enter city first"}
              disabled={!formData.city.trim()}
              onChange={(zip) => {
                const normalizedZip = zip.replace(/\D/g, "").slice(0, 5);
                setFormData((prev) => ({ ...prev, zip: normalizedZip, county: "" }));
              }}
              onBlur={() => setTouched((prev) => ({ ...prev, zip: true }))}
            />

            <div className="space-y-1.5">
              <label
                htmlFor="representative-phone"
                className="block text-sm font-medium text-gray-700"
              >
                Phone <span className="text-status-error">*</span>
              </label>
              <PhoneNumberInput
                id="representative-phone"
                value={formData.phone}
                title={visibleErrors.phone}
                onChange={(phone) => setFormData((prev) => ({ ...prev, phone }))}
                onBlur={() => setPhoneTouched(true)}
                placeholder="Enter phone number"
                aria-invalid={!!visibleErrors.phone}
              />
              {visibleErrors.phone && <p className="text-sm text-status-error">{visibleErrors.phone}</p>}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="representative-email"
                className="block text-sm font-medium text-gray-700"
              >
                Email <span className="text-status-error">*</span>
              </label>
              <Input
                id="representative-email"
                type="email"
                value={formData.email}
                title={visibleErrors.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                onBlur={() => setEmailTouched(true)}
                placeholder="Enter email"
                aria-invalid={!!visibleErrors.email}
              />
              {visibleErrors.email && <p className="text-sm text-status-error">{visibleErrors.email}</p>}
            </div>

            {submitError && (
              <div className="rounded-lg border border-status-error bg-red-50 px-4 py-3 text-sm text-status-error">
                {submitError}
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 border-t border-gray-200 px-6 py-6">
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || (submitAttempted && hasValidationErrors)}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};
